import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import FieldQty from '@/components/FieldQty'
import Price from '@/components/Price'
import type { Product, ProductConsumerCategory } from '@/lib/api.types'
import type { TicketPrice } from '@/types/tickets'

import {
  TicketTypeCardHeader,
  TicketTypeCardOldPrice,
  TicketTypeCardPrice,
  TicketTypeCardQty,
  TicketTypeCardWrapper
} from './TicketTypeCard.styles'

const normalizePrice = (value: unknown) => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return 0
  }

  return numericValue >= 0 ? numericValue : 0
}

type ProductConsumerCategoryWithPricing = ProductConsumerCategory & {
  lowestPriceIn30Days?: {
    grossAmount?: string
  }
}

const extractTicketPrice = (
  product: Product,
  consumerCategory: ProductConsumerCategoryWithPricing | null,
  fallbackCurrency?: string
): TicketPrice => {
  const primaryCategory = (consumerCategory ?? product.consumerCategories?.[0]) as
    | {
        price?: {
          grossAmount?: string
          currency?: string
          taxPercentage?: number
          tax?: {
            value?: string
          }
        }
        lowestPriceIn30Days?: {
          grossAmount?: string
        }
      }
    | undefined

  const grossAmount = normalizePrice(primaryCategory?.price?.grossAmount ?? product.price?.grossAmount ?? product.defaultPrice)
  const lowestGrossAmountIn30Days = normalizePrice(
    primaryCategory?.lowestPriceIn30Days?.grossAmount ?? primaryCategory?.price?.grossAmount ?? grossAmount
  )
  const taxPercentage = normalizePrice(
    primaryCategory?.price?.tax?.value ?? primaryCategory?.price?.taxPercentage ?? product.price?.taxPercentage
  )
  const currency = primaryCategory?.price?.currency || product.price?.currency || fallbackCurrency || 'PLN'

  return {
    grossAmount,
    currency,
    taxPercentage,
    lowestGrossAmountIn30Days
  }
}

interface TicketTypeCardProps {
  product: Product
  consumerCategory: ProductConsumerCategory
  qty: number
  currency?: string
  disablePlus?: boolean
  disableMinus?: boolean
  onQtyChange: (qty: number) => void
}

const TicketTypeCard = ({
  product,
  consumerCategory,
  qty,
  currency: currencyFallback,
  disablePlus = false,
  disableMinus = false,
  onQtyChange
}: TicketTypeCardProps) => {
  const { t } = useTranslation()
  const price = useMemo(
    () => extractTicketPrice(product, consumerCategory as ProductConsumerCategoryWithPricing, currencyFallback),
    [consumerCategory, currencyFallback, product]
  )
  const { lowestGrossAmountIn30Days, grossAmount, currency } = price

  const handleQtyChange = (value: number) => {
    onQtyChange(Math.max(0, value))
  }

  return (
    <TicketTypeCardWrapper>
      <TicketTypeCardHeader>
        <h4>{consumerCategory?.name || product.name}</h4>
        <p>{consumerCategory?.description || product.description}</p>
      </TicketTypeCardHeader>
      <TicketTypeCardPrice>
        <Price currency={currency}>{grossAmount}</Price>
        <TicketTypeCardOldPrice>
          {t('lowest30DaysPrice', {
            price: `${Number(lowestGrossAmountIn30Days)?.toFixed(2)} ${currency}`
          })}
        </TicketTypeCardOldPrice>
      </TicketTypeCardPrice>
      <TicketTypeCardQty>
        <FieldQty value={qty} onChange={handleQtyChange} disablePlus={disablePlus} disableMinus={disableMinus} />
      </TicketTypeCardQty>
    </TicketTypeCardWrapper>
  )
}

export default TicketTypeCard
